import React, { memo, useCallback, useContext, useEffect, useState } from 'react';

import { Box, Flex, Heading, Stack } from '@chakra-ui/layout';
import { Button } from 'components';

import { BodyProps } from 'constants/MultipleAcorddions';
import { ThirdPartyAssessmentCtx } from 'pages';
import { checkPermission } from 'utils/authentication';
import { getAllClients } from 'services/client';

const mSections = [
  {
    title: 'Avaliação de Maturidade:',
    text: 'Traz visibilidade inicial sobre o grau de maturidade do parceiro/fornecedor em privacidade e proteção de dados, a partir de informações básicas, que costumam ser partes integrantes de programas de privacidade.',
  },
  {
    title: 'Teste Controlador - Operador:',
    text: 'Define o enquadramento da {{cliente}} e da parte contrária enquanto agentes de tratamento de dados pessoais. Para cada uma das partes, há 2 opções: Controlador ou Operador (ver definições abaixo).',
  },
  {
    title: 'Teste de Risco do Contrato:',
    text: 'Estabelece score de risco para o contrato, com base em informações básicas, definindo o processo de análise e a medida de mitigação de risco.',
  },
  {
    title: 'Relatório de Análise:',
    text: 'Relatório final, com o compilado de todas as informações levantadas neste documento. Essa resposta não dispensa, em qualquer hipótese, o apoio jurídico especializado, pois elaborada a partir de diretrizes gerais e sem considerar contextos específicos.',
  },
];

const concepts = [
  {
    title: 'Dado Pessoal',
    text: 'Qualquer informação que possa levar à identificação de uma pessoa, seja de maneira direta ou indireta. Inclui: dados cadastrais, hábitos, preferências, histórico de consumo, dados bancários e financeiros, logs e registros eletrônicos, geolocalização, IP, etc.',
  },
  {
    title: 'Titular',
    text: 'Pessoa física a qual se referem os dados pessoais.',
  },
  {
    title: 'Tratamento',
    text: 'Qualquer atividade praticada com dados pessoais. Inclui: acesso, coleta, visualização, compartilhamento, exclusão, armazenamento, etc.',
  },
  {
    title: 'Controlador',
    text: 'Pessoa natural ou jurídica a quem competem as decisões referentes ao tratamento de dados pessoais (parte que decide "como" e "por que" tratar os dados em uma relação contratual).',
  },
  {
    title: 'Co-Controlador',
    text: 'Pessoa natural ou jurídica terceira a quem também competem (ainda que parcialmente) parte das decisões referentes ao tratamento de dados pessoais (parte que decide "como" e "por que" tratar os dados em uma relação contratual).',
  },
  {
    title: 'Operador',
    text: 'Pessoa natural ou jurídica que realiza o tratamento de dados pessoais em nome do controlador (parte que apenas executa uma atividade de tratamento de dados requisitada pela outra).',
  },
];

const Item: React.FC<BodyProps> = ({ functionCallback, index, completed }) => {
  const ctx = useContext(ThirdPartyAssessmentCtx);
  const [clients, setClients] = useState<{ name: string; id: React.ReactText }[]>([]);

  const handleSubmit = useCallback(async () => {
    functionCallback(index + 1);
  }, [functionCallback, index]);

  useEffect(() => {
    if (checkPermission({ onlyAdmin: true })) {
      (async () => {
        const { data } = await getAllClients('');
        setClients(data);
      })();
    }
  }, []);

  useEffect(() => {
    if (ctx.isDetails && clients.length > 0) {
      if (ctx.setClient) ctx.setClient(clients.find(i => i.id === ctx.data.clientId));
    }
  }, [ctx.isDetails, clients]);

  return (
    <Flex marginTop="1rem" width="100%" direction="column">
      <Box>
        <Heading
          mt={checkPermission({ onlyAdmin: true }) && !ctx.isDetails ? '0rem' : undefined}
          mb="1rem"
          size="xs"
          as="p"
          color="brand.primary"
        >
          Objeto
        </Heading>
        <p>
          Este é um documento produzido pela Daniel Advogados que visa auxiliar a análise de
          contratos da {ctx.client?.name} cujos objetos envolvem, de alguma forma, o tratamento de
          dados pessoais.
        </p>
        <Heading color="brand.primary" mt="32px" mb="1rem" size="xs" as="p">
          Método
        </Heading>
        <p>Neste documento, há quatro seções:</p>
        {mSections.map((i, k) => (
          <Box
            borderRadius="4px"
            key={k}
            mt="16px"
            width="100%"
            p="16px"
            border="1px solid"
            borderColor="brand.gray600"
          >
            <Heading color="brand.primary" mb=".5rem" size="xs" as="p">
              {i.title}
            </Heading>
            <p>{i.text.replaceAll('{{cliente}}', (ctx.client?.name || '') + '')}</p>
          </Box>
        ))}
        <Heading color="brand.primary" mt="32px" mb="1rem" size="xs" as="p">
          Resultado
        </Heading>
        <Box as="p" mb="8px">
          Os resultados dos testes devem ser confirmados internamente, considerando a importância e
          relevância estratégica do contrato para a companhia, constituindo mero indicativo, sem se
          confundir com opinião jurídica ou auditoria.
        </Box>
        <p>
          Recomendamos que os testes sejam avaliados pelo Encarregado pelo Tratamento de Dados
          Pessoais da companhia ou consultores jurídicos especializados, que poderão,
          periodicamente, realizar o acompanhamento de cada operação conforme o risco e a relevância
          de cada contrato.
        </p>
        <Heading color="brand.primary" mt="32px" mb="1rem" size="xs" as="p">
          Conceitos Relevantes
        </Heading>
        {concepts.map((i, k) => (
          <Box
            borderRadius="4px"
            key={k}
            mt="16px"
            width="100%"
            p="16px"
            border="1px solid"
            borderColor="brand.gray600"
          >
            <Heading color="brand.primary" mb=".5rem" size="xs" as="p">
              {i.title}
            </Heading>
            <p>{i.text.replaceAll('{{cliente}}', (ctx.client?.name || '') + '')}</p>
          </Box>
        ))}
      </Box>
      {!completed && (
        <Box width="100%">
          <Stack direction={['column', 'row']} spacing="1rem" mt="1rem" justify="flex-end">
            <Button type="button" variant="primary" onClick={handleSubmit}>
              Próximo
            </Button>
          </Stack>
        </Box>
      )}
      {/* </Unform> */}
    </Flex>
  );
};

export const Introduction = memo(Item);
